/*
 * Button to open any <custom-select> on click
 * Used on PDP, ATC button with "Select size" label
 */
class CustomSelectOpener extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) {
      console.error('[custom-select-opener] connected but no <button> found inside it; click will never bind.', this);
      return;
    }
    console.log('[custom-select-opener] connected; target =', this.getAttribute('data-target'));
    this.button.addEventListener('click', (event) => this.openPicker(event));
    this.onVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('variant-change', this.onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant-change', this.onVariantChange);
  }

  openPicker(event) {
    // Prevents target's "click to close" listener firing and closing before we see it
    event?.stopPropagation();

    const selector = this.getAttribute('data-target');

    if (!selector) {
      console.error('[custom-select-opener] no data-target attribute', this);
      return;
    }

    const target = document.querySelector(selector);
    if (!target) {
      console.error(`[custom-select-opener] target "${selector}" not found`);
      return;
    }

    if (typeof target.open !== 'function') {
      console.error(`[custom-select-opener] target "${selector}" has no open() method`);
      return;
    }

    target.open();
  }

  onVariantChange(event) {
    if (this.done || !event.detail?.currentVariant) return;
    this.done = true;
    this.hidden = true;

    // reveal the real ATC unless a BIS opener currently owns the slot (sold-out + BIS)
    const scope = this.closest('product-form') || document;
    const addButton = scope.querySelector('[name="add"]');
    const bisButton = scope.querySelector('bis-opener button');
    if (addButton && (!bisButton || bisButton.hidden)) addButton.hidden = false;
  }
}

customElements.define('custom-select-opener', CustomSelectOpener);

/*
 * <select> replacement with full visual control
 * Syncs to hidden <select> for a11y and form data
 */
class CustomSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-custom-select-source]');
    this.trigger = this.querySelector('[data-custom-select-trigger]');
    this.label = this.querySelector('[data-custom-select-label]');
    this.indicator = this.querySelector('[data-custom-select-indicator]');
    this.list = this.querySelector('[data-custom-select-list]');
    this.options = Array.from(this.querySelectorAll('[data-custom-menu-option]'));

    if (!this.select || !this.trigger || !this.list) return;

    this.trigger.addEventListener('click', () => this.toggle());
    this.options.forEach((option) => option.addEventListener('click', () => this.choose(option)));
    this.select.addEventListener('change', () => this.syncFromSelect());

    // close when focus lost / ESC pressed
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });

    this.syncFromSelect();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  toggle() {
    if (this.list.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.list.hidden = false;
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.list.hidden = true;
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  choose(option) {
    const { value } = option.dataset;
    if (this.select.value !== value) {
      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.close();
  }

  syncFromSelect() {
    const { value } = this.select;
    const selectedOption = this.select.selectedOptions[0];
    if (this.label && selectedOption) this.label.textContent = selectedOption.textContent.trim();

    let matched = null;
    this.options.forEach((option) => {
      const isSelected = option.dataset.value === value;
      option.classList.toggle('is-selected', isSelected);
      if (isSelected) matched = option;
    });

    if (this.indicator) {
      let text = '';
      let isPreorder = false;
      if (matched && value) {
        if (matched.dataset.notify !== undefined) text = this.dataset.labelSoldOut || '';
        else if (matched.dataset.preorder !== undefined) {
          text = this.dataset.labelPreorder || '';
          isPreorder = true;
        } else if (matched.dataset.lowStock !== undefined) text = this.dataset.labelLowStock || '';
      }
      this.indicator.textContent = text;
      this.indicator.classList.toggle('is-preorder', isPreorder);
    }
  }
}

customElements.define('custom-select', CustomSelect);
